import React, { useState, useEffect } from 'react';
import {
  ThemeContext,
  Theme,
  SubThemes,
  Pages,
  DisplayMode,
  TooltipMode,
  ThemeConfig,
} from './ThemeContext';

interface ThemeProviderProps {
  children: React.ReactNode;
}

const MAX_RETRIES = 3;
const RETRY_INTERVAL = 200; // 200 ms
const DEFAULT_THEME = 'high-contrast-monochrome';
const DEFAULT_SUBTHEME = 'monochrome-elegance';

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  function resolveThemeConfig(
    loadedThemes?: Theme,
    loadedSubThemes?: SubThemes
  ) {
    const storedTheme = localStorage.getItem('theme');
    const storedSubTheme = localStorage.getItem('subTheme');

    const fallback = {
      defaultTheme: DEFAULT_THEME,
      defaultSubTheme: DEFAULT_SUBTHEME,
    };

    // Before config is loaded, return the stored pair if present,
    // otherwise return the full default pair.
    if (!loadedThemes || !loadedSubThemes || Object.keys(loadedThemes).length === 0) {
      if (storedTheme) {
        return {
          defaultTheme: storedTheme,
          defaultSubTheme: storedSubTheme || '',
        };
      }
      return fallback;
    }

    // If no stored theme, use the full default pair.
    if (!storedTheme) {
      return fallback;
    }

    // If stored theme does not exist, use the full default pair.
    if (!loadedThemes[storedTheme]) {
      return fallback;
    }

    // If stored theme exists and has a valid stored subtheme, use it.
    if (
      storedSubTheme &&
      loadedSubThemes[storedTheme] &&
      loadedSubThemes[storedTheme][storedSubTheme]
    ) {
      return {
        defaultTheme: storedTheme,
        defaultSubTheme: storedSubTheme,
      };
    }

    // If the stored theme has no valid stored subtheme, fall back to the full default pair.
    return fallback;
  }

  const initialConfig = resolveThemeConfig();

  const [currentTheme, setCurrentTheme] = useState<string>(initialConfig.defaultTheme);
  const [currentSubTheme, setCurrentSubTheme] = useState<string>(initialConfig.defaultSubTheme);

  const [themes, setThemes] = useState<Theme>({});
  const [subThemes, setSubThemes] = useState<SubThemes>({});
  const [pages, setPages] = useState<Pages>({});
  const [tooltipMode, setTooltipMode] = useState<TooltipMode>(TooltipMode.Hover);
  const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.Both);

  const removeExistingThemeLinks = () => {
    const existingThemeLinks = document.querySelectorAll('link[data-theme]');
    existingThemeLinks.forEach(link => {
      const themeAttr = link.getAttribute('data-theme');
      if (themeAttr !== 'common' && themeAttr !== 'default') {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }
    });

    const existingSubThemeLinks = document.querySelectorAll('link[data-subtheme]');
    existingSubThemeLinks.forEach(link => {
      const subThemeAttr = link.getAttribute('data-subtheme');
      if (subThemeAttr !== 'common' && subThemeAttr !== 'default') {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }
    });
  };

  const dispatchThemeChangeEvent = (theme: string, subTheme: string) => {
    let retries = 0;
    const retryInterval = setInterval(() => {
      if (retries < MAX_RETRIES) {
        const event = new CustomEvent('themechange', {
          detail: {
            theme,
            subTheme,
          },
        });
        document.dispatchEvent(event);
        retries++;
      } else {
        clearInterval(retryInterval);
      }
    }, RETRY_INTERVAL);
  };

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const response = await fetch('/config/themes.json');
        const data: ThemeConfig = await response.json();

        setThemes(data.themes);
        setSubThemes(data.subThemes);
        setPages(data.pages);
        setDisplayMode(data.displayMode);
        setTooltipMode(data.tooltipMode);

        const resolvedConfig = resolveThemeConfig(data.themes, data.subThemes);

        // Clean any previously injected links before setting the validated pair
        removeExistingThemeLinks();

        setCurrentTheme(resolvedConfig.defaultTheme);
        setCurrentSubTheme(resolvedConfig.defaultSubTheme);
      } catch (error) {
        console.error('Error fetching themes:', error);
      }
    };

    fetchThemes();
  }, []);

  useEffect(() => {
    if (Object.keys(themes).length === 0) {
      return;
    }

    const themeFile = themes[currentTheme]?.file;
    const subThemeFile = subThemes[currentTheme]?.[currentSubTheme]?.file;

    // Always clean first before applying the new validated theme/subtheme
    removeExistingThemeLinks();

    if (themeFile) {
      const themeLink = document.createElement('link');
      themeLink.rel = 'stylesheet';
      themeLink.href = `/assets/css/${themeFile}`;
      themeLink.setAttribute('data-theme', currentTheme);
      document.head.appendChild(themeLink);
    }

    if (subThemeFile && currentSubTheme) {
      const subThemeLink = document.createElement('link');
      subThemeLink.rel = 'stylesheet';
      subThemeLink.href = `/assets/css/${subThemeFile}`;
      subThemeLink.setAttribute('data-subtheme', currentSubTheme);
      document.head.appendChild(subThemeLink);
    }

    localStorage.setItem('theme', currentTheme);
    localStorage.setItem('subTheme', currentSubTheme);

    setTimeout(() => {
      dispatchThemeChangeEvent(currentTheme, currentSubTheme);
    }, 200);
  }, [currentTheme, currentSubTheme, themes, subThemes]);

  const changeTheme = (theme: string, subTheme?: string) => {
    const requestedSubTheme = subTheme ? subTheme : '';

    let resolvedTheme = theme;
    let resolvedSubTheme = requestedSubTheme;

    // If selected theme does not exist, fall back to full default pair
    if (!themes[resolvedTheme]) {
      resolvedTheme = DEFAULT_THEME;
      resolvedSubTheme = DEFAULT_SUBTHEME;
    } else {
      // If provided subtheme is invalid for the selected theme, fall back to the full default pair
      if (
        resolvedSubTheme &&
        (!subThemes[resolvedTheme] || !subThemes[resolvedTheme][resolvedSubTheme])
      ) {
        resolvedTheme = DEFAULT_THEME;
        resolvedSubTheme = DEFAULT_SUBTHEME;
      }
    }

    // Clean old links before changing state
    removeExistingThemeLinks();

    localStorage.setItem('theme', resolvedTheme);
    localStorage.setItem('subTheme', resolvedSubTheme);

    setCurrentTheme(resolvedTheme);
    setCurrentSubTheme(resolvedSubTheme);

    setTimeout(() => {
      dispatchThemeChangeEvent(resolvedTheme, resolvedSubTheme);
    }, 200);
  };

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        currentSubTheme,
        themes,
        subThemes,
        pages,
        displayMode,
        tooltipMode,
        changeTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
